import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "../i18n";

export const LanguageSwitcher = ({ variant = "light" }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];
  const isDark = variant === "dark";

  const triggerClass = isDark
    ? "text-white/70 hover:text-white border-white/20 hover:border-white/40"
    : "text-[#5D544F] hover:text-[#C1624D] border-[#D8D0C5] hover:border-[#C1624D]";

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${triggerClass}`}
        style={{ fontFamily: "'Lato', sans-serif" }}
        data-testid="language-switcher-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={13} />
        <span>{current.code.toUpperCase()}</span>
      </button>
      {open && (
        <ul
          className="absolute right-0 mt-2 min-w-[160px] bg-white border border-[#D8D0C5] rounded-sm shadow-lg overflow-hidden z-50"
          role="listbox"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <li key={lang.code}>
                <button
                  onClick={() => {
                    i18n.changeLanguage(lang.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors duration-200 ${
                    active ? "bg-[#F5E6D3] text-[#C1624D]" : "text-[#2C2420] hover:bg-[#FDFBF7]"
                  }`}
                  style={{ fontFamily: "'Lato', sans-serif" }}
                  data-testid={`language-option-${lang.code}`}
                  role="option"
                  aria-selected={active}
                >
                  <span className="text-base leading-none">{lang.flag}</span>
                  <span className="flex-1">{lang.label}</span>
                  {active && <Check size={14} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
